/**
 * Outreach API
 *
 * API client for Communications Hub / OutreachLog endpoints.
 * Used by Monitor view to display preview messages.
 */

import axios from 'axios';

const NEXUS_API_BASE = import.meta.env.VITE_NEXUS_API || 'http://localhost:3007';

/**
 * OutreachLog entry with student info
 */
/**
 * Signal breakdown for risk and potential scores
 */
export interface SignalBreakdown {
  risk: {
    inactivity?: number;
    cliffDrop?: number;
    stuck?: number;
    negativeSentiment?: number;
    pace?: number;
    technicalIssue?: number;
    contactPenalty?: number;
  };
  potential: {
    completion?: number;
    recentActivity?: number;
    streak?: number;
    activityDays?: number;
    trajectory?: number;
    positiveSentiment?: number;
  };
}

/**
 * Opt-out status for participant communication channels
 */
export interface OptOutStatus {
  sms: boolean;
  email: boolean;
}

export interface OutreachLogEntry {
  id: string;
  studentId: string;
  studentName?: string;
  studentAvatar?: string;
  programId: string;
  triggerType: string;
  triggeredAt: Date;
  riskScore: number;
  potentialScore: number;
  quadrant: string;
  messageContent: string | null; // Nullable for on-demand generation
  messageChannel: string;
  recommendation: string;
  status: string;
  programName?: string; // Added for aggregate view
  // Signal breakdown for score transparency
  signals?: SignalBreakdown;
  // Score at last engagement (for delta tracking)
  scoreAtLastEngagement?: number;
  // Response tracking per message
  responseReceived?: boolean;
  // Delivery tracking (MVP-1: Message Dispatch)
  sentAt?: Date;
  deliveredAt?: Date;
  failureReason?: string;
  externalId?: string;
  // Thread tracking (MVP-1.0.1: Conversation Threading)
  threadId?: string;
  // Opt-out status (Task 8: Visual Indicator)
  optedOut?: OptOutStatus;
  // On-demand message generation (Task 6)
  messageGeneratedAt?: Date;
  // Scheduled outreach (AIPM behavioral integration - Task 13)
  scheduledFor?: Date | string;
  jobId?: string | null;
  // Optimal send time from BehavioralAnalytics (for Send Now vs Schedule options)
  scheduledSendAt?: Date | string | null;
}

/**
 * Quadrant statistics
 */
export interface OutreachStats {
  total: number;
  byQuadrant: {
    URGENT: number;
    STRUGGLING: number;
    PASSIVE: number;
    THRIVING: number;
  };
}

/**
 * Aggregate outreach statistics (multi-program)
 */
export interface AggregateOutreachStats extends OutreachStats {
  byProgram: {
    programId: string;
    programName: string;
    count: number;
  }[];
}

/**
 * Response from generate preview messages endpoint
 */
export interface GeneratePreviewResponse {
  success: boolean;
  programsProcessed: number;
  messagesGenerated: number;
  errors: string[];
}

/**
 * Query options for fetching outreach log
 */
export interface OutreachQueryOptions {
  status?: string;
  quadrant?: string;
  dateRange?: string;
  limit?: number;
  offset?: number;
}

/**
 * Fetch outreach log entries for a program
 *
 * @param programId - Program ID
 * @param options - Query options
 * @returns Array of outreach log entries
 */
export async function fetchOutreachLog(
  programId: string,
  options: OutreachQueryOptions = {}
): Promise<OutreachLogEntry[]> {
  try {
    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.quadrant) params.append('quadrant', options.quadrant);
    if (options.dateRange) params.append('dateRange', options.dateRange);
    if (options.limit) params.append('limit', String(options.limit));
    if (options.offset) params.append('offset', String(options.offset));

    const response = await axios.get(
      `${NEXUS_API_BASE}/api/programs/${programId}/outreach?${params.toString()}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching outreach log:', error);
    throw error;
  }
}

/**
 * Fetch quadrant statistics for a program
 *
 * @param programId - Program ID
 * @returns Quadrant statistics
 */
export async function fetchOutreachStats(programId: string): Promise<OutreachStats> {
  try {
    const response = await axios.get(`${NEXUS_API_BASE}/api/programs/${programId}/outreach/stats`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching outreach stats:', error);
    throw error;
  }
}

/**
 * Fetch outreach log entries across ALL user's programs (aggregate)
 *
 * @param options - Query options
 * @returns Array of outreach log entries with program name
 */
export async function fetchAggregateOutreachLog(
  options: OutreachQueryOptions = {}
): Promise<OutreachLogEntry[]> {
  const params = new URLSearchParams();
  if (options.status) params.append('status', options.status);
  if (options.quadrant) params.append('quadrant', options.quadrant);
  if (options.dateRange) params.append('dateRange', options.dateRange);
  if (options.limit) params.append('limit', String(options.limit));
  if (options.offset) params.append('offset', String(options.offset));

  const url = `${NEXUS_API_BASE}/api/outreach?${params.toString()}`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache'
      },
      params: { _t: Date.now() } // Cache-bust to avoid 304 stale data
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error fetching aggregate outreach log:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Engagement statistics for first-principles engagement health metrics
 */
export interface EngagementStats {
  /** Total messages sent (approved and delivered) */
  totalSent: number;
  /** Total unique participants contacted */
  totalContacted: number;
  /** Total participants who have replied at least once */
  totalReplied: number;
  /** Response rate as percentage (0-100) */
  responseRate: number;
  /** Conversations awaiting reply (have unread inbound messages) */
  awaitingReply: number;
  /** Total active conversation threads */
  totalConversations: number;
}

/**
 * Fetch quadrant statistics across ALL user's programs (aggregate)
 *
 * @returns Aggregate outreach statistics with byProgram breakdown
 */
export async function fetchAggregateOutreachStats(): Promise<AggregateOutreachStats> {
  const url = `${NEXUS_API_BASE}/api/outreach/stats`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error fetching aggregate outreach stats:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Fetch engagement health metrics across ALL user's programs
 *
 * First-principles metrics for stakeholder visibility:
 * - Response rate: % of contacted participants who have replied
 * - Total conversations: Active conversation threads
 * - Awaiting reply: Conversations with unread inbound messages
 *
 * @returns Engagement statistics for header display
 */
export async function fetchEngagementStats(): Promise<EngagementStats> {
  const url = `${NEXUS_API_BASE}/api/outreach/engagement-stats`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error fetching engagement stats:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Generate preview messages for all user's programs
 *
 * This triggers the AI to analyze all participants and generate
 * personalized outreach messages. Messages are saved with status='preview'
 * and will NOT be sent automatically.
 *
 * @returns Generation results (programs processed, messages generated, errors)
 */
export async function generatePreviewMessages(): Promise<GeneratePreviewResponse> {
  const url = `${NEXUS_API_BASE}/api/outreach/generate`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(
      url,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error generating preview messages:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Response from generate-for-students endpoint (async 202 Accepted)
 */
export interface GenerateForStudentsResponse {
  accepted: boolean;
  totalStudents: number;
  message: string;
}

/**
 * Enqueue async message generation for specific students (filtered/visible ones).
 * Returns 202 Accepted immediately. Poll getGenerateMessagesStatus() for progress.
 *
 * @param studentIds - Array of student IDs to generate messages for
 * @param programId - Optional program ID for context
 * @returns Acceptance status with total students queued
 */
export async function generateMessagesForStudents(
  studentIds: string[],
  programId?: string
): Promise<GenerateForStudentsResponse> {
  const url = `${NEXUS_API_BASE}/api/outreach/generate-for-students`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(
      url,
      { studentIds, programId },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        timeout: 30000 // 30s - endpoint returns 202 immediately now
      }
    );

    return response.data;
  } catch (error: any) {
    if (error.code === 'ECONNABORTED') {
      console.error('[outreachApi] generateMessagesForStudents timed out');
    }
    console.error('[outreachApi] Error generating messages for students:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

// ============================================================================
// Auto-Pilot API
// ============================================================================

/**
 * AutoPilot configuration
 */
export interface AutoPilotViewPreferences {
  groupBy: 'quadrant' | 'risk' | 'activity' | 'list';
  sortBy: 'Risk' | 'Potential' | 'Completion' | 'Name';
  showOnlineOnly: boolean;
}

/**
 * Escalation configuration for auto-pilot system
 */
export interface EscalationConfig {
  enabled: boolean;
  weights: {
    consecutiveNoResponse: number; // default 0.4
    riskScoreIncrease: number; // default 0.4
    inactivityDays: number; // default 0.2
  };
  thresholds: {
    consecutiveNoResponse: number; // default 3
    riskScoreIncreaseDelta: number; // default 15
    inactivityDays: number; // default 21
  };
}

/**
 * Date range options for auto-pilot scope filtering
 */
export type AutoPilotDateRange =
  | 'all'
  | 'last7'
  | 'last14'
  | 'last30'
  | 'last60'
  | 'previous'
  | 'custom';

/**
 * Auto-pilot scope configuration
 */
export interface AutoPilotScope {
  type: 'program' | 'participant' | 'multi-program' | 'multi-participant' | 'all';
  programIds?: string[];
  studentIds?: string[];
  // Date range filtering
  dateRange?: AutoPilotDateRange;
  customDateStart?: string; // ISO date string for custom range
  customDateEnd?: string; // ISO date string for custom range
  schoolYear?: string; // e.g., "2024-2025" for previous school year selection
}

export interface AutoPilot {
  _id: string;
  enabledBy: string;
  enabledByRole: 'creator' | 'sponsor' | 'participant';
  programId?: string;
  programName?: string;
  deprecated?: boolean;
  scope: AutoPilotScope;
  channels: {
    sms: {
      enabled: boolean;
      managerPreference: boolean;
    };
    email: {
      enabled: boolean;
      managerPreference: boolean;
    };
  };
  triggerThresholds: {
    minRiskScore?: number;
    maxRiskScore?: number;
    minPotentialScore?: number;
    quadrants: Array<'URGENT' | 'STRUGGLING' | 'PASSIVE' | 'THRIVING'>;
    recommendations?: string[];
  };
  settings: {
    mode: 'preview' | 'review' | 'auto-send';
  };
  viewPreferences?: AutoPilotViewPreferences;
  escalationConfig?: EscalationConfig;
  participantOverrides?: {
    autoEnabled: string[];
    excluded: string[];
    enabledAt?: Record<string, string>;
    excludedAt?: Record<string, string>;
  };
  conversationSettings?: {
    conversationHistoryDepth: number;
    conversationRateLimit: {
      enabled: boolean;
      maxMessagesPerHour?: number | null;
      maxMessagesPerDay?: number | null;
    };
  };
  writeBackConfig?: {
    enabled: boolean;
    completionMode: 'draft' | 'field_by_field' | 'complete';
    confidenceThresholds: {
      autoApprove: number;
      recommend: number;
      humanDecision: number;
    };
    safetyLimits: {
      maxWritesPerParticipantPerDay: number;
      maxWritesPerProgramPerHour: number;
    };
    activityOverrides?: Record<string, unknown>;
    notifications: {
      notifyParticipantOnWrite: boolean;
      notifyPMOnAutoWrite: boolean;
    };
    feedback?: {
      enabled: boolean;
      qualityThreshold?: number;
      maxIterations?: number;
    };
  };
  contextCapabilities?: {
    programContext: { enabled: boolean };
    activityContext: { enabled: boolean };
    resourceContext: { enabled: boolean };
    longRangeContext?: {
      enabled: boolean;
      spanThreshold?: number;
      maxRawContentChars?: number;
    };
  };
  onboardingConfig?: {
    enabled?: boolean;
    defaultPersonaId?: string;
    defaultPersonaIds?: string[];
    required: boolean;
    customWelcome?: string;
    phoneRequired: boolean;
    retroactiveEnabled?: boolean;
    retroactiveEnabledAt?: string;
    welcomeMessageEnabled?: boolean;
  };
  communityChatConfig?: {
    enabled: boolean;
    senderPersonaId?: string | null;
    aiPresenceMode: 'summoned' | 'always';
    moderation: {
      enabled: boolean;
      enableContentFilter: boolean;
      enableParticipantReporting: boolean;
      showModerationActions: boolean;
      autoHideReportedMessages: boolean;
    };
  };
  deadlineThresholds?: {
    enabled: boolean;
    presets: {
      hours24Before: boolean;
      hours4Before: boolean;
      whenOverdue: boolean;
    };
    customHours: number[];
  };
  status: 'active' | 'paused';
  createdAt: string;
  updatedAt: string;
}

/**
 * DTO for creating AutoPilot
 */
export interface CreateAutoPilotDto {
  programId: string; // Required for 1:1 relationship
  programName?: string; // Human-readable program name for display purposes
  scope?: AutoPilot['scope']; // Optional for backward compatibility
  channels: AutoPilot['channels'];
  triggerThresholds: AutoPilot['triggerThresholds'];
  settings: AutoPilot['settings'];
  viewPreferences?: AutoPilotViewPreferences;
  escalationConfig?: EscalationConfig;
  deadlineThresholds?: AutoPilot['deadlineThresholds'];
}

/**
 * DTO for updating AutoPilot
 */
export interface UpdateAutoPilotDto {
  scope?: AutoPilotScope;
  channels?: AutoPilot['channels'];
  triggerThresholds?: AutoPilot['triggerThresholds'];
  settings?: AutoPilot['settings'];
  viewPreferences?: AutoPilotViewPreferences;
  escalationConfig?: EscalationConfig;
  participantOverrides?: AutoPilot['participantOverrides'];
  conversationSettings?: AutoPilot['conversationSettings'];
  writeBackConfig?: Partial<NonNullable<AutoPilot['writeBackConfig']>>;
  contextCapabilities?: AutoPilot['contextCapabilities'];
  onboardingConfig?: AutoPilot['onboardingConfig'];
  communityChatConfig?: AutoPilot['communityChatConfig'];
  deadlineThresholds?: AutoPilot['deadlineThresholds'];
}

/**
 * Coverage check result
 */
export interface CoverageCheckResult {
  covered: boolean;
  coveredBy?: {
    name: string;
    role: string;
  };
}

/**
 * Pilot status response for permission checks
 */
export interface PilotStatusResponse {
  isPilot: boolean;
  isCoPilot: boolean;
  canManageAutoPilot: boolean;
}

/**
 * Can message student response
 */
export interface CanMessageResponse {
  canMessage: boolean;
  reason?: string;
}

/**
 * Create a new auto-pilot configuration
 */
export async function createAutoPilot(data: CreateAutoPilotDto): Promise<AutoPilot> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(url, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error creating auto-pilot:', error);
    throw error;
  }
}

/**
 * Dashboard: Get all programs the user can manage + their AutoPilot configs.
 * Single endpoint replaces N+1 individual calls.
 */
export interface DashboardProgram {
  programId: string;
  programName: string;
  role: 'creator' | 'organizer';
  studentCount: number;
  autoPilot: AutoPilot | null;
}

export interface DashboardResponse {
  programs: DashboardProgram[];
}

export async function getAutoPilotDashboard(): Promise<DashboardResponse> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/dashboard`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error fetching dashboard:', error);
    throw error;
  }
}

/**
 * List all auto-pilot configurations for the current user
 */
export async function listAutoPilots(): Promise<AutoPilot[]> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      timeout: 60000 // 60 second timeout (increased for slow connections)
    });

    return response.data;
  } catch (error: any) {
    console.error(
      '[outreachApi] Error listing auto-pilots:',
      error?.message,
      error?.code,
      error?.response?.status
    );
    // Return empty array on timeout/error to prevent UI crash
    // The UI will show no auto-pilots which is a graceful degradation
    if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
      console.warn('[outreachApi] Auto-pilot request timed out, returning empty array');
      return [];
    }
    throw error;
  }
}

/**
 * Get a single auto-pilot by ID
 */
export async function getAutoPilot(id: string): Promise<AutoPilot> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/${id}`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error getting auto-pilot:', error);
    throw error;
  }
}

/**
 * Batch fetch auto-pilot configurations for multiple programs in a single request.
 *
 * Replaces N+1 individual getAutoPilotByProgramId calls with one batch call.
 *
 * @param programIds - Array of program IDs
 * @returns Array of AutoPilot configurations (only programs with configs)
 */
export async function getAutoPilotsByProgramIds(programIds: string[]): Promise<AutoPilot[]> {
  if (!programIds || programIds.length === 0) {
    return [];
  }

  const token = localStorage.getItem('apollo-token');
  const BATCH_SIZE = 80; // Backend allows max 100, use 80 for safety margin

  // Split into chunks to avoid backend 100-ID limit and URL length issues
  const chunks: string[][] = [];
  for (let i = 0; i < programIds.length; i += BATCH_SIZE) {
    chunks.push(programIds.slice(i, i + BATCH_SIZE));
  }

  // Use Promise.allSettled so one failing chunk doesn't lose all results
  const chunkResults = await Promise.allSettled(
    chunks.map(async chunk => {
      const url = `${NEXUS_API_BASE}/api/auto-pilot/by-programs?ids=${chunk.join(',')}`;
      const response = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        timeout: 15000
      });

      const { data } = response;
      if (Array.isArray(data)) {
        return data;
      }
      if (data && typeof data === 'object') {
        return Object.values(data).filter((ap): ap is AutoPilot => ap !== null);
      }
      return [];
    })
  );

  const results: AutoPilot[] = chunkResults.reduce<AutoPilot[]>((acc, result) => {
    if (result.status === 'fulfilled') {
      acc.push(...result.value);
    } else {
      console.warn(
        '[outreachApi] One auto-pilot batch chunk failed:',
        result.reason?.message || result.reason
      );
    }
    return acc;
  }, []);
  return results;
}

/**
 * Get auto-pilot configuration for a specific program
 *
 * Returns the AutoPilot configuration for a program, or null if
 * no configuration exists yet (not configured state).
 *
 * @param programId - Program ID
 * @returns AutoPilot configuration or null
 */
export async function getAutoPilotByProgramId(programId: string): Promise<AutoPilot | null> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/by-program/${programId}`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    // Return null for 404 (not configured)
    if (error.response?.status === 404) {
      return null;
    }
    console.error('[outreachApi] Error getting auto-pilot by program:', error);
    throw error;
  }
}

/**
 * Update an existing auto-pilot
 */
export async function updateAutoPilot(id: string, data: UpdateAutoPilotDto): Promise<AutoPilot> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/${id}`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.patch(url, data, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error updating auto-pilot:', error);
    throw error;
  }
}

/**
 * Delete an auto-pilot
 */
export async function deleteAutoPilot(id: string): Promise<void> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/${id}`;
  const token = localStorage.getItem('apollo-token');

  try {
    await axios.delete(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error: any) {
    console.error('[outreachApi] Error deleting auto-pilot:', error);
    throw error;
  }
}

/**
 * Toggle auto-pilot status (active/paused)
 */
export async function toggleAutoPilotStatus(
  id: string,
  status: 'active' | 'paused'
): Promise<AutoPilot> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/${id}/status`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.patch(
      url,
      { status },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error toggling auto-pilot status:', error);
    throw error;
  }
}

/**
 * Check if a student is covered by an existing auto-pilot
 */
export async function checkCoverage(studentId: string): Promise<CoverageCheckResult> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/check-coverage/${studentId}`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error checking coverage:', error);
    throw error;
  }
}

/**
 * Update escalation configuration for an auto-pilot
 */
export async function updateEscalationConfig(
  autoPilotId: string,
  escalationConfig: EscalationConfig
): Promise<AutoPilot> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/${autoPilotId}/escalation-config`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.patch(
      url,
      { escalationConfig },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error updating escalation config:', error);
    throw error;
  }
}

/**
 * Get pilot status for a program
 *
 * Checks if the current user is a Pilot, Co-pilot, or has no role
 * for the specified program.
 *
 * @param programId - Program ID
 * @returns Pilot status response with permission flags
 */
export async function getPilotStatus(programId: string): Promise<PilotStatusResponse> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/pilot-status/${programId}`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error getting pilot status:', error);
    throw error;
  }
}

/**
 * Check if current user can message a specific student
 *
 * Validates if the user is a Pilot for the student's program OR
 * a Co-pilot (stakeholder) for that specific student.
 *
 * @param studentId - Student ID
 * @returns Can message response with reason if denied
 */
export async function canMessageStudent(studentId: string): Promise<CanMessageResponse> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/can-message-student/${studentId}`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error checking message permission:', error);
    throw error;
  }
}

// ============================================================================
// Message Review API (MVP-1: Human Review)
// ============================================================================

/**
 * Approve an outreach message for sending
 *
 * Sets status to 'approved' and queues the message for delivery.
 *
 * @param id - OutreachLog ID
 * @param sendNow - If true (default), send immediately. If false, schedule for optimal time.
 * @returns Updated OutreachLogEntry
 */
export async function approveOutreachMessage(
  id: string,
  sendNow: boolean = true
): Promise<OutreachLogEntry> {
  const url = `${NEXUS_API_BASE}/api/smart-trigger/outreach/${id}/approve`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(
      url,
      { sendNow },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error approving outreach message:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Resend a failed outreach message
 *
 * Resets status from 'failed' to 'approved' and re-queues for delivery.
 *
 * @param id - OutreachLog ID
 * @returns Updated OutreachLogEntry
 */
export async function resendOutreachMessage(id: string): Promise<OutreachLogEntry> {
  const url = `${NEXUS_API_BASE}/api/smart-trigger/outreach/${id}/resend`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(
      url,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error resending outreach message:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Reject an outreach message
 *
 * Sets status to 'rejected'. Message will not be sent.
 *
 * @param id - OutreachLog ID
 * @returns Updated OutreachLogEntry
 */
export async function rejectOutreachMessage(id: string): Promise<OutreachLogEntry> {
  const url = `${NEXUS_API_BASE}/api/smart-trigger/outreach/${id}/reject`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(
      url,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error rejecting outreach message:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Edit the content of an outreach message
 *
 * Updates the message content. Does NOT auto-approve (separate action required).
 *
 * @param id - OutreachLog ID
 * @param content - New message content
 * @returns Updated OutreachLogEntry
 */
export async function editOutreachMessage(id: string, content: string): Promise<OutreachLogEntry> {
  const url = `${NEXUS_API_BASE}/api/smart-trigger/outreach/${id}/edit`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.put(
      url,
      { messageContent: content },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error editing outreach message:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

// ============================================================================
// Inbound Messages API (MVP-1.0.1: Conversation Threading)
// ============================================================================

/**
 * InboundMessage entry (participant reply)
 */
export interface InboundMessageEntry {
  id: string;
  channel: string;
  content: string;
  subject?: string | null;
  fromAddress: string;
  studentId?: string | null;
  studentName?: string;
  programId?: string | null;
  inReplyToOutreachId?: string | null;
  threadId?: string | null;
  status: string;
  receivedAt: Date;
  createdAt: Date;
}

/**
 * Query options for fetching inbound messages
 */
export interface InboundMessageQueryOptions {
  studentId?: string;
  status?: string;
  since?: string;
  limit?: number;
}

/**
 * Fetch inbound messages (participant replies) for a program
 *
 * @param programId - Program ID
 * @param options - Query options
 * @returns Array of inbound message entries
 */
export async function fetchInboundMessages(
  programId: string,
  options: InboundMessageQueryOptions = {}
): Promise<InboundMessageEntry[]> {
  try {
    const params = new URLSearchParams();
    if (options.studentId) params.append('studentId', options.studentId);
    if (options.status) params.append('status', options.status);
    if (options.since) params.append('since', options.since);
    if (options.limit) params.append('limit', String(options.limit));

    const response = await axios.get(
      `${NEXUS_API_BASE}/api/smart-trigger/programs/${programId}/inbound-messages?${params.toString()}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching inbound messages:', error);
    throw error;
  }
}

/**
 * Query options for aggregate inbound messages
 */
export interface AggregateInboundQueryOptions {
  status?: string;
  channel?: string;
  limit?: number;
  offset?: number;
}

/**
 * Fetch inbound messages across ALL user's programs (aggregate)
 *
 * @param options - Query options
 * @returns Array of inbound message entries with student and program name
 */
export async function fetchAggregateInboundMessages(
  options: AggregateInboundQueryOptions = {}
): Promise<InboundMessageEntry[]> {
  const params = new URLSearchParams();
  if (options.status) params.append('status', options.status);
  if (options.channel) params.append('channel', options.channel);
  if (options.limit) params.append('limit', String(options.limit));
  if (options.offset) params.append('offset', String(options.offset));

  const url = `${NEXUS_API_BASE}/api/inbound-messages?${params.toString()}`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error fetching aggregate inbound messages:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Inbound message count response
 */
export interface InboundMessageCountResponse {
  total: number;
  unread: number;
}

/**
 * Fetch count of inbound messages across ALL user's programs
 *
 * @returns Count with total and unread breakdown
 */
export async function fetchInboundMessageCount(): Promise<InboundMessageCountResponse> {
  const url = `${NEXUS_API_BASE}/api/inbound-messages/count`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error fetching inbound message count:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Thread message (from thread conversation view)
 */
export interface ThreadMessage {
  id: string;
  type: 'outreach' | 'inbound';
  channel: string;
  content: string;
  subject?: string | null;
  timestamp: Date;
  status?: string;
  sentAt?: Date | null;
  deliveredAt?: Date | null;
  failureReason?: string | null;
}

/**
 * Thread metadata
 */
export interface ThreadInfo {
  id: string;
  studentId: string;
  programId: string;
  studentName: string;
  programName: string;
  studentEmail: string | null;
  studentPhone: string | null;
  threadEmail: string | null;
  threadPhone: string | null;
  status: string;
  lastMessageAt: Date;
  messageCount: number;
}

/**
 * Conversation thread summary (for list view)
 */
export interface ConversationThreadSummary {
  id: string;
  studentId: string;
  programId: string;
  studentName: string;
  programName: string;
  lastChannel: string | null;
  lastMessageAt: Date;
  messageCount: number;
  status: string;
  lastMessagePreview: string | null;
  lastMessageDirection: 'outbound' | 'inbound' | null;
  hasUnread: boolean;
}

/**
 * List threads response
 */
export interface ListThreadsResponse {
  threads: ConversationThreadSummary[];
  total: number;
}

/**
 * Query options for listing threads
 */
export interface ListThreadsOptions {
  limit?: number;
  offset?: number;
  programId?: string;
}

/**
 * Thread messages response
 */
export interface ThreadMessagesResponse {
  thread: ThreadInfo;
  messages: ThreadMessage[];
}

/**
 * Fetch thread ID by student and program
 *
 * @param studentId - Student ID
 * @param programId - Program ID
 * @returns Thread ID or throws error if not found
 */
export async function fetchThreadByStudent(
  studentId: string,
  programId: string
): Promise<{ id: string }> {
  try {
    const response = await axios.get(
      `${NEXUS_API_BASE}/api/programs/threads/by-student/${studentId}/${programId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching thread by student:', error);
    throw error;
  }
}

/**
 * Fetch all messages in a conversation thread
 *
 * @param threadId - Thread ID
 * @returns Thread info and chronologically sorted messages
 */
export async function fetchThreadMessages(threadId: string): Promise<ThreadMessagesResponse> {
  try {
    const response = await axios.get(
      `${NEXUS_API_BASE}/api/programs/threads/${threadId}/messages`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching thread messages:', error);
    throw error;
  }
}

/**
 * Scheduled outreach message entry
 * Represents a pending/approved message awaiting send
 */
export interface ScheduledOutreachEntry {
  id: string;
  studentId: string;
  programId: string;
  messageContent: string | null;
  messageChannel: string;
  status: 'preview' | 'pending_review' | 'approved';
  triggeredAt: Date | string;
  scheduledSendAt: Date | string | null;
  quadrant: string;
  riskScore: number;
  potentialScore: number;
  recommendation: string | null;
  triggerType: string;
}

/**
 * Fetch scheduled/pending outreach messages for a student
 *
 * Returns OutreachLog entries with status 'preview', 'pending_review', or 'approved'
 * that haven't been sent yet. Used by ConversationView to show upcoming scheduled messages.
 *
 * @param programId - Program ID
 * @param studentId - Student ID
 * @returns Array of scheduled outreach entries
 */
export async function fetchScheduledOutreach(
  programId: string,
  studentId: string
): Promise<ScheduledOutreachEntry[]> {
  try {
    const response = await axios.get(
      `${NEXUS_API_BASE}/api/programs/${programId}/outreach/scheduled/${studentId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('apollo-token')}`
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching scheduled outreach:', error);
    throw error;
  }
}

/**
 * Fetch all conversation threads for the current user
 *
 * @param options - Pagination options (limit, offset)
 * @returns List of thread summaries with total count
 */
export async function fetchConversationThreads(
  options: ListThreadsOptions = {}
): Promise<ListThreadsResponse> {
  const params = new URLSearchParams();
  if (options.programId) params.append('programId', options.programId);
  if (options.limit) params.append('limit', String(options.limit));
  if (options.offset) params.append('offset', String(options.offset));

  const url = `${NEXUS_API_BASE}/api/programs/threads?${params.toString()}`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error fetching conversation threads:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

// ============================================================================
// Scoring API
// ============================================================================

/**
 * Response from score-all endpoint (202 Accepted - async job queued)
 */
export interface ScoreAllResponse {
  accepted: boolean;
  totalStudents: number;
  message: string;
}

// ============================================================================
// Compose Message API (Manual Compose)
// ============================================================================

/**
 * Request body for compose message
 */
export interface ComposeMessageRequest {
  studentIds: string[];
  messageContent: string;
  channel: 'sms' | 'email';
  subject?: string;
}

/**
 * Response from compose message endpoint
 */
export interface ComposeMessageResponse {
  success: boolean;
  messagesSent: number;
  outreachLogIds: string[];
}

/**
 * Compose and send a manual message to one or more participants.
 *
 * Creates OutreachLog entries with triggerType='manual_compose',
 * auto-approves them, and queues for immediate sending.
 *
 * @param programId - Program ID
 * @param request - Compose request with studentIds, messageContent, channel
 * @returns Result with messagesSent count and outreachLogIds
 */
export async function composeMessage(
  programId: string,
  request: ComposeMessageRequest
): Promise<ComposeMessageResponse> {
  const url = `${NEXUS_API_BASE}/api/programs/${programId}/outreach/compose`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(url, request, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error composing message:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

// ============================================================================
// Scoring API
// ============================================================================

/**
 * Score all participants the current user has access to.
 *
 * Enqueues an async scoring job. Returns 202 immediately.
 * Poll getScoreAllStatus() for progress.
 *
 * @returns Accepted response with totalStudents count
 */
export async function scoreAllParticipants(): Promise<ScoreAllResponse> {
  const url = `${NEXUS_API_BASE}/api/outreach/score-all`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(
      url,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        timeout: 30000
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error queuing score-all:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Response from score-all status polling endpoint
 */
export interface ScoreAllStatusResponse {
  inProgress: boolean;
  scored: number;
  failed: number;
  total: number;
}

/**
 * Poll the score-all progress for the current user.
 * Returns real-time progress as students are scored one by one on the backend.
 */
export async function getScoreAllStatus(): Promise<ScoreAllStatusResponse> {
  const url = `${NEXUS_API_BASE}/api/outreach/score-all/status`;
  const token = localStorage.getItem('apollo-token');

  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`
    },
    timeout: 5000
  });

  return response.data;
}

/**
 * Response from generate-messages status polling endpoint
 */
export interface GenerateMessagesStatusResponse {
  inProgress: boolean;
  processed: number;
  total: number;
  messagesGenerated: number;
}

/**
 * Poll the generate-messages progress for the current user.
 * Returns real-time progress as students are processed batch by batch on the backend.
 */
export async function getGenerateMessagesStatus(): Promise<GenerateMessagesStatusResponse> {
  const url = `${NEXUS_API_BASE}/api/outreach/generate-messages/status`;
  const token = localStorage.getItem('apollo-token');

  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`
    },
    timeout: 5000
  });

  return response.data;
}

/**
 * Response from refresh-scores status polling endpoint
 */
export interface RefreshScoresStatusResponse {
  inProgress: boolean;
  processed: number;
  total: number;
  refreshed: number;
  skipped: number;
  failed: number;
}

/**
 * Poll the refresh-scores progress for the current user.
 */
export async function getRefreshScoresStatus(): Promise<RefreshScoresStatusResponse> {
  const url = `${NEXUS_API_BASE}/api/outreach/refresh-scores/status`;
  const token = localStorage.getItem('apollo-token');

  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`
    },
    timeout: 5000
  });

  return response.data;
}

/**
 * Response from refresh-scores endpoint
 */
export interface RefreshScoresResponse {
  success: boolean;
  refreshed: number;
  skippedDebounce: number;
  failed: number;
  scores: Array<{
    studentId: string;
    riskScore: number;
    potentialScore: number;
    quadrant: string;
    recommendation: string;
    scoredAt: Date;
  }>;
}

/**
 * Refresh scores for specific students (filtered/visible ones only).
 *
 * This is more efficient than scoreAllParticipants() as it only scores
 * the students you specify, not ALL students the user has access to.
 *
 * Implements debounce: max 1 refresh per student per 5 minutes.
 *
 * @param studentIds - Array of student IDs to refresh
 * @param programId - Program ID (for logging/context)
 * @returns Scoring results with updated scores
 */
export async function refreshStudentScores(
  studentIds: string[],
  programId: string
): Promise<RefreshScoresResponse> {
  const url = `${NEXUS_API_BASE}/api/outreach/refresh-scores`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(
      url,
      { studentIds, programId },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        // 10 minute timeout - scoring 1000+ students one-by-one takes time
        timeout: 600000
      }
    );

    return response.data;
  } catch (error: any) {
    if (error.code === 'ECONNABORTED') {
      console.error('[outreachApi] refreshStudentScores timed out after 60 seconds');
    }
    console.error('[outreachApi] Error refreshing student scores:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

// ============================================================================
// Smart Channel API (Intelligent Channel Selection)
// ============================================================================

/**
 * Channel statistics for a student
 */
export interface ChannelStats {
  sent: number;
  responses: number;
  responseRate: number;
}

/**
 * Smart channel recommendation for a student
 */
export interface StudentChannelRecommendation {
  studentId: string;
  recommendedChannel: 'sms' | 'email';
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
  stats: {
    sms: ChannelStats;
    email: ChannelStats;
  };
  availableChannels: {
    sms: boolean;
    email: boolean;
  };
}

/**
 * Response from smart channel recommendations endpoint
 */
export interface SmartChannelResponse {
  recommendations: StudentChannelRecommendation[];
}

/**
 * Get smart channel recommendations for a list of students.
 *
 * Analyzes engagement history to determine the optimal channel (SMS vs Email)
 * for each student based on response rates.
 *
 * @param programId - Program ID
 * @param studentIds - Array of student IDs to get recommendations for
 * @returns Array of channel recommendations with confidence and reasoning
 */
export async function getSmartChannelRecommendations(
  programId: string,
  studentIds: string[]
): Promise<StudentChannelRecommendation[]> {
  const url = `${NEXUS_API_BASE}/api/programs/${programId}/outreach/smart-channel`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(
      url,
      { studentIds },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data.recommendations;
  } catch (error: any) {
    console.error('[outreachApi] Error getting smart channel recommendations:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

// ============================================================================
// Behavioral Analytics API (Task 10 & 11: ML-Lite + ML-Full)
// ============================================================================

/**
 * Engagement heatmap - day of week (0-6) to hour (0-23) to count
 */
export type EngagementHeatmap = Record<string, Record<string, number>>;

/**
 * Optimal contact window recommendation
 */
export interface OptimalContactWindow {
  hour: number;
  minute: number;
  confidence: number;
}

/**
 * Rhythm type - when student is most active
 */
export type RhythmType =
  | 'dawn_worker'
  | 'night_owl'
  | 'lunch_warrior'
  | 'weekend_sprinter'
  | 'variable';

/**
 * Momentum type - how student engages best
 */
export type MomentumType = 'micro_touch' | 'weekly_deep' | 'milestone' | 'peer_accountability';

/**
 * Breakthrough achievement for confidence curation
 */
export interface Breakthrough {
  timestamp: Date;
  context: string;
  achievement: string;
  sentiment: 'positive' | 'neutral' | 'negative';
}

/**
 * Urgency level for outreach recommendation
 */
export type OutreachUrgency = 'low' | 'medium' | 'high' | 'urgent';

/**
 * Message style for outreach (matches momentum type)
 */
export type OutreachMessageStyle =
  | 'micro_touch'
  | 'weekly_deep'
  | 'milestone'
  | 'peer_accountability';

/**
 * Emotional tone for outreach
 */
export type OutreachEmotionalTone = 'encouraging' | 'celebratory' | 'supportive' | 'urgent';

/**
 * Relationship stage with student
 */
export type RelationshipStage = 'early' | 'developing' | 'established';

/**
 * Cohort momentum context for peer-based insights
 */
export interface CohortMomentum {
  recentCompletions: number;
  participantInfluence: number;
  cohortPosition: 'leading' | 'middle' | 'trailing';
}

/**
 * Momentum pattern metrics
 */
export interface MomentumPattern {
  dailyActivity: number; // 0-1 scale
  weeklyBursts: number; // 0-1 scale
  deadlineCorrelation: number; // 0-1 scale
}

/**
 * Next recommended outreach scheduling context
 */
export interface NextRecommendedOutreach {
  scheduledFor: Date | string;
  dayOfWeek?: number;
  hour?: number;
  minute?: number;
  timezone?: string;
  urgency: OutreachUrgency;
  urgencyReason?: string;
  messageStyle: OutreachMessageStyle;
  emotionalTone: OutreachEmotionalTone;
  relationshipStage: RelationshipStage;
  suggestedBreakthrough?: Breakthrough | null;
  cohortMomentum?: CohortMomentum;
  confidence: number; // 0-1 scale
  rationale?: string; // AI's reasoning
  computedAt?: Date | string;
}

/**
 * Scheduled outreach entry (from OutreachLog with BullMQ job ID)
 */
export interface ScheduledOutreach {
  scheduledFor: Date | string;
  channel: 'sms' | 'email';
  jobId?: string | null;
  outreachLogId?: string;
  message?: string;
}

/**
 * Behavioral status for a participant (Task 13.1)
 * Combines BehavioralAnalytics with scheduled outreach information
 */
export interface BehavioralStatus {
  // Trust and relationship metrics
  trustScore: number;
  relationshipStage: RelationshipStage;
  repairMode: boolean;
  dormant: boolean;
  dormantSince?: Date | string | null;
  thresholdMultiplier?: number;
  // Scheduled outreach (from OutreachLog)
  scheduledOutreach?: ScheduledOutreach | null;
  // Metadata
  lastUpdated?: Date | string;
}

/**
 * Message preview response (Task 13.1)
 */
export interface MessagePreview {
  message: string;
  behavioralContext: {
    trustScore: number;
    relationshipStage: RelationshipStage;
    repairMode: boolean;
    dormant: boolean;
    recommendedChannel: 'sms' | 'email';
    scheduledFor?: Date | string;
  };
  rationale: string;
  generatedAt: Date | string;
}

/**
 * Behavioral analytics for a student in a program
 */
export interface BehavioralAnalytics {
  studentId: string;
  programId: string;
  participantId?: string;
  // Denormalized participant data (from backend)
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  programName?: string;
  // Chronobiology
  engagementHeatmap: EngagementHeatmap;
  optimalContactWindow: OptimalContactWindow;
  rhythmType: RhythmType;
  // Momentum
  momentumType: MomentumType;
  currentMomentum?: number;
  momentumPattern?: MomentumPattern;
  // Drift Risk
  driftRiskScore: number;
  responseTimeTrend?: {
    trend: 'improving' | 'stable' | 'declining';
  };
  sentimentTrend?: number[]; // Array of sentiment scores over time
  lastDriftAlert?: Date | string;
  // Breakthroughs (Confidence Curation)
  breakthroughs?: Breakthrough[];
  // Relationship
  relationshipMetrics?: {
    totalInteractions: number;
    avgResponseTime: number;
    positiveExchanges: number;
    trustScore: number;
  };
  // Next Recommended Outreach (Auto-Pilot integration)
  nextRecommendedOutreach?: NextRecommendedOutreach;
  lastOutreachAt?: Date | string;
  // Metadata
  dataPointCount: number;
  lastUpdated?: Date | string;
  createdAt?: Date | string;
}

/**
 * Aggregate behavioral analytics summary
 */
export interface AggregateBehavioralAnalytics {
  totalStudents: number;
  byRhythmType: Record<RhythmType, number>;
  byMomentumType: Record<MomentumType, number>;
  averageDriftRiskScore: number;
  highRiskCount: number; // driftRiskScore >= 70
  students: BehavioralAnalytics[];
}

/**
 * Fetch behavioral analytics for a specific student in a program
 *
 * @param studentId - Student ID
 * @param programId - Program ID
 * @returns Behavioral analytics data
 */
export async function fetchBehavioralAnalytics(
  studentId: string,
  programId: string
): Promise<BehavioralAnalytics | null> {
  const url = `${NEXUS_API_BASE}/api/smart-trigger/behavioral-analytics/${studentId}/${programId}`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      // No analytics data yet - return null
      return null;
    }
    console.error('[outreachApi] Error fetching behavioral analytics:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Fetch aggregate behavioral analytics for all students the user has access to
 *
 * Uses POST to avoid URL length limits when filtering by many studentIds.
 * With 400+ students, GET query params can exceed browser URL limits (~8000 chars).
 *
 * @param programIds - Optional array of program IDs to filter by
 * @param studentIds - Optional array of student IDs to filter by (from filtered participant list)
 * @returns Aggregate behavioral analytics data
 */
export async function fetchAggregateBehavioralAnalytics(
  programIds?: string[],
  studentIds?: string[]
): Promise<AggregateBehavioralAnalytics> {
  const url = `${NEXUS_API_BASE}/api/smart-trigger/behavioral-analytics/aggregate`;
  const token = localStorage.getItem('apollo-token');

  try {
    // Use POST to send studentIds in request body (avoids URL length limits)
    const response = await axios.post(
      url,
      {
        programIds: programIds || [],
        studentIds: studentIds || []
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error fetching aggregate behavioral analytics:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    // Return empty aggregate on error
    return {
      totalStudents: 0,
      byRhythmType: {
        dawn_worker: 0,
        night_owl: 0,
        lunch_warrior: 0,
        weekend_sprinter: 0,
        variable: 0
      },
      byMomentumType: {
        micro_touch: 0,
        weekly_deep: 0,
        milestone: 0,
        peer_accountability: 0
      },
      averageDriftRiskScore: 0,
      highRiskCount: 0,
      students: []
    };
  }
}

/**
 * Request body for smart compose
 */
export interface SmartComposeRequest {
  studentIds: string[];
  messageContent: string;
  subject?: string;
  channelOverrides?: Record<string, 'sms' | 'email'>; // studentId -> channel override
}

/**
 * Response from smart compose endpoint
 */
export interface SmartComposeResponse {
  success: boolean;
  messagesSent: number;
  outreachLogIds: string[];
  channelBreakdown: {
    sms: number;
    email: number;
  };
}

/**
 * Smart compose and send messages using optimal channels per recipient.
 *
 * Automatically selects the best channel (SMS vs Email) for each student
 * based on engagement history, unless overridden.
 *
 * @param programId - Program ID
 * @param request - Smart compose request with optional channel overrides
 * @returns Result with breakdown of channels used
 */
export async function smartCompose(
  programId: string,
  request: SmartComposeRequest
): Promise<SmartComposeResponse> {
  const url = `${NEXUS_API_BASE}/api/programs/${programId}/outreach/smart-compose`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(url, request, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error smart composing:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

// ============================================================================
// AIPM Behavioral Analytics Integration (Task 13)
// ============================================================================

/**
 * Preview outreach message for a specific participant without sending (Task 13.2)
 *
 * Generates a personalized message using behavioral analytics context
 * but does NOT schedule or send it. Useful for previewing what Auto-Pilot
 * would generate before enabling auto-send mode.
 *
 * @param autoPilotId - Auto-Pilot configuration ID
 * @param participantId - Student/Participant ID
 * @returns Message preview with behavioral context and AI rationale
 */
export async function previewOutreachMessage(
  autoPilotId: string,
  participantId: string
): Promise<MessagePreview> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/${autoPilotId}/preview-message`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(
      url,
      { participantId },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error previewing outreach message:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Cancel a scheduled outreach message (Task 13.4)
 *
 * Removes the BullMQ job from the queue and updates OutreachLog status.
 * This is used when Auto-Pilot schedules a message but the user wants
 * to cancel it before it's sent.
 *
 * @param outreachLogId - OutreachLog entry ID
 * @returns void (success) or throws error
 */
export async function cancelScheduledOutreach(outreachLogId: string): Promise<void> {
  const url = `${NEXUS_API_BASE}/api/smart-trigger/outreach/${outreachLogId}/cancel-scheduled`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(
      url,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );
  } catch (error: any) {
    console.error('[outreachApi] Error cancelling scheduled outreach:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Generate preview message for a specific OutreachLog entry (on-demand)
 *
 * Uses the stored contextSnapshot to generate an AI message for preview.
 * This is called when the user clicks "Generate Preview" on a queue item
 * that doesn't have a message yet.
 *
 * @param outreachLogId - OutreachLog entry ID
 * @param useCurrentContext - If true, refresh context from current data (default: false)
 * @returns Generated message content and updated OutreachLog
 */
export async function generatePreviewForOutreach(
  outreachLogId: string,
  useCurrentContext = false
): Promise<{ message: string; outreachLog: OutreachLogEntry }> {
  const url = `${NEXUS_API_BASE}/api/smart-trigger/outreach/${outreachLogId}/generate-preview`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(
      `${url}?useCurrentContext=${useCurrentContext}`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error generating preview for outreach:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Update the message channel for an OutreachLog entry
 *
 * Allows toggling between SMS and email before sending.
 *
 * @param outreachLogId - OutreachLog entry ID
 * @param channel - 'sms' or 'email'
 * @returns Updated OutreachLog
 */
export async function updateOutreachChannel(
  outreachLogId: string,
  channel: 'sms' | 'email'
): Promise<OutreachLogEntry> {
  const url = `${NEXUS_API_BASE}/api/smart-trigger/outreach/${outreachLogId}`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.patch(
      url,
      { messageChannel: channel },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error updating outreach channel:', {
      error: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
    throw error;
  }
}

/**
 * Training Mode Participant Interface
 */
export interface TrainingModeParticipant {
  studentId: string;
  studentName: string;
  programId: string;
  programName: string;
  avatar?: string;
  quadrant: 'URGENT' | 'STRUGGLING' | 'PASSIVE' | 'THRIVING';
  riskScore: number;
  potentialScore: number;
  status: 'would-send' | 'no-action' | 'auto-enabled' | 'excluded';
  urgency?: 'urgent' | 'high' | 'medium' | 'low';
  urgencyReason?: string;
  noActionReason?: string;
  nextRecommendedOutreach?: {
    scheduledFor: Date;
    channel: string;
    confidence: number;
    rhythmType?: string;
    reasonForTiming?: string;
  };
  behavioralContext?: {
    relationshipStage: string;
    momentumType: string;
    emotionalTone: string;
    rhythmType: string;
    trustScore: number;
  };
  suggestedBreakthrough?: string;
  lastContactedAt?: Date;
  signalBreakdown?: {
    riskSignals: string[];
    potentialSignals: string[];
  };
}

/**
 * Training Mode View Response Interface
 */
export interface TrainingModeViewResponse {
  participants: TrainingModeParticipant[];
  stats: {
    wouldSend: number;
    noAction: number;
    autoEnabled: number;
    excluded: number;
  };
  lastUpdated: string | Date;
}

/**
 * Participant Override Request
 */
export interface ParticipantOverrideRequest {
  studentId: string;
  action: 'enable-auto' | 'exclude' | 'clear';
}

/**
 * Message Preview Response
 */
export interface MessagePreviewResponse {
  message: string;
  behavioralContext: {
    messageStyle: 'micro_touch' | 'weekly_deep' | 'milestone' | 'standard';
    emotionalTone: 'celebratory' | 'encouraging' | 'curious' | 'direct' | 'gentle';
    relationshipStage: 'early' | 'developing' | 'established' | 'mature';
    suggestedBreakthrough?: any;
  };
  rationale: string;
  generatedAt: Date;
}

/**
 * Fetch Training Mode View data for an AutoPilot
 *
 * @param autoPilotId - AutoPilot ID
 * @returns Training Mode View data with participants grouped by status
 */
export async function fetchTrainingModeView(
  autoPilotId: string
): Promise<TrainingModeViewResponse> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/${autoPilotId}/training-mode-view`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error fetching training mode view:', error);
    throw error;
  }
}

/**
 * Set participant override (enable-auto, exclude, or clear)
 *
 * @param autoPilotId - AutoPilot ID
 * @param request - Override request
 * @returns Updated AutoPilot configuration
 */
export async function setParticipantOverride(
  autoPilotId: string,
  request: ParticipantOverrideRequest
): Promise<AutoPilot> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/${autoPilotId}/participant-override`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(url, request, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error setting participant override:', error);
    throw error;
  }
}

/**
 * Preview message for a participant
 *
 * @param autoPilotId - AutoPilot ID
 * @param studentId - Student ID
 * @returns Message preview with behavioral context
 */
export async function previewParticipantMessage(
  autoPilotId: string,
  studentId: string
): Promise<MessagePreviewResponse> {
  const url = `${NEXUS_API_BASE}/api/auto-pilot/${autoPilotId}/participant/${studentId}/preview-message`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error previewing participant message:', error);
    throw error;
  }
}

/**
 * Detailed opt-out status for a user (with timestamps)
 */
export interface UserOptOutStatus {
  smsOptedOut: boolean;
  emailOptedOut: boolean;
  smsOptedOutAt: Date | string | null;
  emailOptedOutAt: Date | string | null;
}

/**
 * Fetch opt-out status for a batch of users
 *
 * @param userIds - Array of user IDs to check
 * @returns Map of userId → UserOptOutStatus
 */
export async function fetchOptOutStatus(
  userIds: string[]
): Promise<Record<string, UserOptOutStatus>> {
  if (!userIds || userIds.length === 0) {
    return {};
  }

  const url = `${NEXUS_API_BASE}/api/outreach/opt-out-status`;
  const token = localStorage.getItem('apollo-token');

  try {
    const response = await axios.post(
      url,
      { userIds },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('[outreachApi] Error fetching opt-out status:', error);
    return {};
  }
}
